import { classMerge, useUnsavedChanges } from "aic";
import { TocSaveIndicator } from "components/toc/TocSaveIndicator";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { useContext } from "react";
import { HierarchyCrumbs } from "./HierarchyCrumbs";
import { NewItemDropMenu } from "./NewItemDropMenu";
import { RightDropMenu } from "./RightDropMenu";
import { SettingsDropMenu } from "./SettingsDropMenu";
import styles from "./TopBar.module.css";

export interface TopBarProps {
  title: string;
  fileEntry?: React.ReactElement;
  settingsEntry?: React.ReactElement;
  showUser?: boolean;
}

/**
 * Top-level application bar containing brand, navigation, user controls, and
 * TOC save indicator.
 *
 * @param props - Destructured `TopBarProps` object.
 * @param props.title - The title text to display in the centre of the top bar.
 * @param props.fileEntry - Optional element inserted into the new-item drop menu area.
 * @param props.settingsEntry - Optional settings element rendered in the settings menu.
 * @param props.showUser - When true, displays the user menu on the right-hand side.
 * @returns A JSX element representing the top application bar.
 */
export const TopBar = ({
  title,
  fileEntry,
  settingsEntry,
  showUser = true,
}: TopBarProps): React.ReactElement => {
  const appSettings = useContext(AppSettingsContext);
  const logo = new URL("../../../public/Mosaic.png", import.meta.url);
  const { navigate } = useUnsavedChanges("top-bar");

  return (
    <div className={styles.grid}>
      <div className={styles.gridBrandCell}>
        <div className={styles.brandHolder}>
          <div
            className={styles.brandBackgroud}
            id="topbar-brand"
            onClick={() => navigate("/")}
          >
            <img
              src={logo.href}
              alt="Mosaic logo"
              title="Mosaic logo"
              width="214"
              height="63"
            />
          </div>
        </div>
      </div>
      <div
        id="topbar-top-row"
        className={classMerge([styles.gridRowOne, "bg-light"])}
      >
        {!appSettings.maintenanceMode && (
          <div className={styles.menuHolder}>
            <NewItemDropMenu fileEntry={fileEntry} />
            <SettingsDropMenu settingsEntry={settingsEntry} />
          </div>
        )}
        <div
          id="topbar-title"
          className={styles.center}
          title={title}
        >
          <span className={classMerge([styles.titleText, "text-dark", "fw-bold"])}>{title}</span>
          <TocSaveIndicator />
        </div>
        {showUser && <RightDropMenu />}
      </div>
      <div className={styles.gridRowTwo}>
        <HierarchyCrumbs />
      </div>
    </div>
  );
};

TopBar.displayName = "TopBar";
